/**
 * The bundle visit chip (tracker item 85): `Spring Lawn Program · 2 of 3`, one
 * colour per enrollment, a link to the enrollment page. Shown wherever a visit
 * job shows. `linked={false}` for roles that cannot open enrollments
 * (technicians) and for print.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import * as types from '../../api/types';
import { chipColourClasses, chipLabel, enrollmentPath, enrollmentRefOf } from './bundleUi';

interface ChipProps {
  enrollmentId: string;
  enrollmentStep: number;
  enrollmentStepCount: number;
  bundleName: string;
  /** Link to /enrollments/:id (default true) */
  linked?: boolean;
  className?: string;
}

const base = 'inline-flex max-w-full items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ring-1 ring-inset';

export function EnrollmentChip({ enrollmentId, enrollmentStep, enrollmentStepCount, bundleName, linked = true, className = '' }: ChipProps) {
  const label = chipLabel({ bundleName, enrollmentStep, enrollmentStepCount });
  const classes = `${base} ${chipColourClasses(enrollmentId)} ${className}`;
  const text = <span className="truncate">{label}</span>;
  if (!linked) {
    return <span data-testid="enrollment-chip" data-enrollment-id={enrollmentId} className={classes} title={`Service bundle visit: ${label}`}>{text}</span>;
  }
  return (
    <Link
      to={enrollmentPath(enrollmentId)}
      data-testid="enrollment-chip"
      data-enrollment-id={enrollmentId}
      className={`${classes} hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500`}
      title={`Open the ${bundleName} enrollment`}
      aria-label={`Service bundle visit ${enrollmentStep} of ${enrollmentStepCount}: ${bundleName}. Open enrollment`}
      onClick={(event) => event.stopPropagation()}
    >
      {text}
    </Link>
  );
}

/** The chip of a job (or board / technician card) that carries the enrollment fields; nothing for other jobs. */
export function JobEnrollmentChip({ job, linked = true, className }: { job: types.JobEnrollmentRef; linked?: boolean; className?: string }) {
  const ref = enrollmentRefOf(job);
  return ref ? <EnrollmentChip {...ref} linked={linked} className={className} /> : null;
}
